import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';

import { useAuth } from '../../../../contexts/AuthContext';
import { STATUS_OPTIONS } from '../../../../hooks/admin/useFallEvents';

const LABEL_OPTIONS = [
  { value: 'actual_help', label: 'Help Needed' },
  { value: 'actual_no_help', label: 'No Help Needed' },
  { value: 'false_alarm', label: 'False Alarm' },
  { value: 'minor_trip', label: 'Minor Trip' },
  { value: 'unclear', label: 'Unclear / Needs Review' },
  { value: 'repeat_fall', label: 'Repeat Fall' },
];

const ADMIN_STATUS_OPTIONS = STATUS_OPTIONS.filter((option) => option.value !== 'ALL');

const FallEventDrawer = ({
  open,
  event,
  onClose,
  onSave,
  buildClipUrl,
}) => {
  const { user } = useAuth();
  const role = user?.role;
  const isClient = role === 'client';
  const canEditAdminFields = !isClient;
  const canEditClientNotes = isClient;
  const [formState, setFormState] = useState(() => mapEventToForm(event));

  useEffect(() => {
    setFormState(mapEventToForm(event));
  }, [event]);

  const clipUrl = useMemo(
    () => (event ? buildClipUrl(event.video_snippet) : null),
    [buildClipUrl, event],
  );

  const overviewText = useMemo(() => {
    if (!event) return '';
    const timestamp = new Date(event.timestamp).toLocaleString();
    const cameraName = event.camera?.name || 'Unknown camera';
    return `${timestamp} - ${cameraName}`;
  }, [event]);

  const detectionLabel = useMemo(() => {
    if (!event) return '';
    return event.detected_by_display
      ? `Detected by ${event.detected_by_display}`
      : 'Detected automatically';
  }, [event]);

  const handleChange = (field, value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    if (!event) return;
    const payload = {};
    if (canEditAdminFields) {
      payload.status = formState.status;
      payload.label = formState.label || null;
      payload.notes = formState.adminNotes || '';
    }
    if (canEditClientNotes) {
      payload.client_notes = formState.clientNotes || '';
    }
    if (Object.keys(payload).length === 0) {
      onClose?.();
      return;
    }
    onSave?.(event.id, payload);
  };

  const clientNotes = formState.clientNotes || event?.client_notes || '';
  const auditTrail = event?.audit_trail || [];
  const labeledAtText = event?.labeled_at ? new Date(event.labeled_at).toLocaleString() : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      PaperProps={{
        sx: {
          width: 'min(720px, 90vw)',
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ px: 3, py: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Fall event details</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ px: 3, py: 3 }}>
        {event ? (
          <Stack spacing={2.5}>
            <Stack spacing={0.75}>
              <Typography variant="subtitle2" color="text.secondary">
                Overview
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                sx={{ gap: 1 }}
              >
                <WarningAmberIcon color="warning" fontSize="small" />
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                  {overviewText}
                </Typography>
                <Chip
                  label={detectionLabel}
                  size="small"
                  variant="outlined"
                  sx={{ mt: { xs: 0.5, sm: 0 } }}
                />
                <Chip
                  label={event.client_name ? `Client: ${event.client_name}` : 'Unassigned client'}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ mt: { xs: 0.5, sm: 0 } }}
                />
              </Stack>
            </Stack>

            <Divider flexItem />

            <Stack spacing={1.25}>
              <Typography variant="subtitle2" color="text.secondary">
                Clip preview
              </Typography>
              {clipUrl ? (
                <Box
                  component="video"
                  controls
                  width="100%"
                  sx={{ borderRadius: 1, maxHeight: 320, backgroundColor: 'background.paper' }}
                >
                  <source src={clipUrl} type="video/mp4" />
                  Your browser does not support the video tag.{' '}
                  <a href={clipUrl} target="_blank" rel="noopener noreferrer">
                    Download clip
                  </a>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No clip attached to this event.
                </Typography>
              )}
            </Stack>

            <Divider flexItem />

            <Stack spacing={1.5}>
              <Typography variant="subtitle2" color="text.secondary">
                Status & labeling
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <FormControl size="small" fullWidth sx={{ flex: 1 }}>
                  <InputLabel id="fall-event-status-label" shrink>Status</InputLabel>
                  <Select disabled={!canEditAdminFields} labelId="fall-event-status-label"
                    value={formState.status}
                    label="Status"
                    onChange={(selectEvent) => handleChange('status', selectEvent.target.value)}
                  >
                    {ADMIN_STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth sx={{ flex: 1 }}>
                  <InputLabel id="fall-event-label-label" shrink>Label</InputLabel>
                  <Select disabled={!canEditAdminFields} labelId="fall-event-label-label"
                    value={formState.label || ''}
                    label="Label"
                    onChange={(selectEvent) => handleChange('label', selectEvent.target.value || '')}
                    displayEmpty
                    renderValue={(value) => {
                      if (!value) {
                        return 'Unlabeled';
                      }
                      const option = LABEL_OPTIONS.find((item) => item.value === value);
                      return option ? option.label : value;
                    }}
                  >
                    <MenuItem value="">
                      <em>Unlabeled</em>
                    </MenuItem>
                    {LABEL_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              {!isClient && (
                <Stack spacing={0.75}>
                  <Typography variant="body2" color="text.secondary">
                    Current label attribution
                  </Typography>
                  {event?.labeled_by_display ? (
                    <Alert severity="info" variant="outlined">
                      {`Last label set by ${event.labeled_by_display}${labeledAtText ? ` on ${labeledAtText}` : ''}.`}
                    </Alert>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No label attribution recorded yet.
                    </Typography>
                  )}
                </Stack>
              )}
            </Stack>

            <Divider flexItem />

            <Stack spacing={1.5}>
              <Typography variant="subtitle2" color="text.secondary">
                Admin notes
              </Typography>
              <TextField disabled={!canEditAdminFields} value={formState.adminNotes}
                onChange={(textEvent) => handleChange('adminNotes', textEvent.target.value)}
                placeholder="Add context for other responders"
                multiline
                minRows={4}
                fullWidth
              />
            </Stack>

            <Stack spacing={1.25}>
              <Typography variant="subtitle2" color="text.secondary">
                Client notes
              </Typography>
              {canEditClientNotes ? (
                <TextField
                  value={formState.clientNotes}
                  onChange={(textEvent) => handleChange('clientNotes', textEvent.target.value)}
                  placeholder="Share any extra context about this incident"
                  multiline
                  minRows={4}
                  fullWidth
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {clientNotes || 'No notes from the client yet.'}
                </Typography>
              )}
            </Stack>

            {!isClient && (
              <>
                <Divider flexItem />

                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <HistoryEduIcon fontSize="small" color="action" />
                    <Typography variant="subtitle2" color="text.secondary">
                      Review history
                    </Typography>
                  </Stack>
                  {auditTrail.length ? (
                    <Stack spacing={1.25}>
                      {auditTrail.map((entry) => {
                        const reviewedAt = entry.reviewed_at ? new Date(entry.reviewed_at).toLocaleString() : 'Unknown time';
                        const reviewedBy = entry.reviewed_by_display || 'Unknown user';
                        const statusChanged = entry.previous_status !== entry.new_status;
                        const labelChanged = entry.previous_label !== entry.new_label;
                        const notesChanged = entry.previous_notes !== entry.new_notes;
                        const clientNotesChanged = entry.previous_client_notes !== entry.new_client_notes;
                        const assigneeChanged = entry.previous_assigned_to !== entry.new_assigned_to;

                        return (
                          <Box
                            key={entry.id}
                            sx={{
                              border: 1,
                              borderColor: 'divider',
                              borderRadius: 1.5,
                              px: 2,
                              py: 1.5,
                              backgroundColor: 'background.paper',
                            }}
                          >
                            <Stack
                              direction={{ xs: 'column', sm: 'row' }}
                              justifyContent="space-between"
                              spacing={0.75}
                              sx={{ mb: 1 }}
                            >
                              <Typography variant="body2" fontWeight={600}>
                                {entry.action_display}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {`${reviewedBy} | ${reviewedAt}`}
                              </Typography>
                            </Stack>

                            <Stack spacing={0.5}>
                              {labelChanged && (
                                <Typography variant="body2" color="text.secondary">
                                  {`Label: ${entry.previous_label || 'Unlabeled'} -> ${entry.new_label || 'Unlabeled'}`}
                                </Typography>
                              )}
                              {statusChanged && (
                                <Typography variant="body2" color="text.secondary">
                                  {`Status: ${entry.previous_status || 'Unknown'} -> ${entry.new_status || 'Unknown'}`}
                                </Typography>
                              )}
                              {assigneeChanged && (
                                <Typography variant="body2" color="text.secondary">
                                  {`Assigned to: ${entry.previous_assigned_to_display || 'Unassigned'} -> ${entry.new_assigned_to_display || 'Unassigned'}`}
                                </Typography>
                              )}
                              {notesChanged && (
                                <Typography variant="body2" color="text.secondary">
                                  Admin notes were updated.
                                </Typography>
                              )}
                              {clientNotesChanged && (
                                <Typography variant="body2" color="text.secondary">
                                  Client notes were updated.
                                </Typography>
                              )}
                              {!labelChanged && !statusChanged && !assigneeChanged && !notesChanged && !clientNotesChanged && (
                                <Typography variant="body2" color="text.secondary">
                                  Review details recorded.
                                </Typography>
                              )}
                            </Stack>
                          </Box>
                        );
                      })}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No review history recorded for this event yet.
                    </Typography>
                  )}
                </Stack>
              </>
            )}
          </Stack>
        ) : (
          <Typography color="text.secondary">Select an event to see details.</Typography>
        )}
      </DialogContent>

      {event && (
        <DialogActions sx={{ px: 3, py: 2, gap: 2 }}>
          <Button variant="outlined" color="inherit" onClick={onClose} fullWidth>
            Close
          </Button>
          <Button variant="contained" onClick={handleSave} fullWidth>
            Save changes
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

const mapEventToForm = (event) => {
  if (!event) {
    return {
      status: STATUS_OPTIONS[0].value,
      label: '',
      adminNotes: '',
      clientNotes: '',
    };
  }

  return {
    status: event.status || STATUS_OPTIONS[0].value,
    label: event.label || '',
    adminNotes: event.notes || '',
    clientNotes: event.client_notes || '',
  };
};

export default FallEventDrawer;
